import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { hydraUiApi } from '@/services/api';
import type { QueryConfig } from '../shared/react-query-types';
import { requireWorkspaceId } from '../shared/workspace';
import { setFetchedCollectionCache } from './cache';
import { collectionItemsKey, collectionKeys, parseCollection, type Collection } from './entity';

export type CollectionItem = Record<string, unknown>;

export const COLLECTION_IMPORT_POLL_INTERVAL_MS = 4000;

export type CollectionItemsResponse = {
  collection_items: CollectionItem[];
  total: number;
};

export function getCollectionImportRefetchInterval(collections: Collection | Collection[] | undefined): number | false {
  const collectionList = Array.isArray(collections) ? collections : collections == null ? [] : [collections];
  return collectionList.some((collection) => collection.importing) ? COLLECTION_IMPORT_POLL_INTERVAL_MS : false;
}

/* ------------------------------------------------------------------------- */
/* List                                                                       */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCollections(workspaceId: number, qc: QueryClient): Promise<Collection[]> {
  const res = await hydraUiApi.collections.list({
    params: { path: { environment_id: String(workspaceId) } },
  });
  const collections = (res.collections ?? []).map(parseCollection);
  // Populate detail slots so a row click resolves without a second fetch.
  for (const c of collections) setFetchedCollectionCache(qc, workspaceId, c);
  return collections;
}

export const getCollectionsQueryOptions = (workspaceId: number, qc: QueryClient) =>
  queryOptions({
    queryKey: collectionKeys.list(workspaceId, undefined),
    queryFn: () => getCollections(workspaceId, qc),
    refetchInterval: (query) => getCollectionImportRefetchInterval(query.state.data),
    staleTime: 0,
  });

/** @public */
export const useCollections = ({
  workspaceId: overrideWorkspaceId,
  queryConfig,
}: { workspaceId?: number; queryConfig?: QueryConfig<typeof getCollectionsQueryOptions> } = {}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getCollectionsQueryOptions(workspaceId, qc), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Detail                                                                     */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCollection(workspaceId: number, id: string, qc: QueryClient): Promise<Collection> {
  const res = await hydraUiApi.collections.get({
    params: { path: { environment_id: String(workspaceId), id } },
  });
  if (res.collection == null) {
    throw new Error(`collection ${id} missing from get-collection response`);
  }
  const collection = parseCollection(res.collection);

  setFetchedCollectionCache(qc, workspaceId, collection);

  return collection;
}

export const getCollectionQueryOptions = (workspaceId: number, id: string, qc: QueryClient) =>
  queryOptions({
    queryKey: collectionKeys.detail(workspaceId, id),
    queryFn: () => getCollection(workspaceId, id, qc),
    refetchInterval: (query) => getCollectionImportRefetchInterval(query.state.data),
    staleTime: 0,
  });

/** @public */
export const useCollection = ({
  id,
  queryConfig,
}: {
  id: string;
  queryConfig?: QueryConfig<typeof getCollectionQueryOptions>;
}) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({ ...getCollectionQueryOptions(workspaceId, id, qc), ...queryConfig });
};

/* ------------------------------------------------------------------------- */
/* Items                                                                      */
/* ------------------------------------------------------------------------- */

export type GetCollectionItemsInput = {
  workspaceId: number;
  collectionId: number;
  page: number;
  limit: number;
  conditions?: string | null;
  orderBy?: string | null;
  asc?: boolean;
};

/**
 * `signal` is a second argument rather than part of `input` because `input` is spread into
 * the query key below — an AbortSignal in there would make every render a cache miss.
 *
 * @public
 */
export async function getCollectionItems(
  input: GetCollectionItemsInput,
  signal?: AbortSignal,
): Promise<CollectionItemsResponse> {
  const { workspaceId, collectionId, page, limit, conditions, orderBy, asc } = input;
  // The generated OpenAPI types still under-document the response body, so it is
  // read through the localized cast below (see services ui_api/collections/query.go
  // CollectionItems for what the endpoint actually returns).
  const response = await hydraUiApi.collections.getCollectionItems({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        collection_id: collectionId,
        limit,
        page,
        conditions: conditions ?? undefined,
        order_by: orderBy ?? undefined,
        asc,
      },
    },
    signal,
  });
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const parsedResponse = response as Partial<{
    collection_items: CollectionItem[] | null;
    total: number | null;
  }>;
  return {
    collection_items: parsedResponse.collection_items ?? [],
    total: parsedResponse.total ?? 0,
  };
}

/** One page of a collection's rows, keyed by everything the read asks for. */
const collectionItemsPageKey = (input: GetCollectionItemsInput) =>
  [...collectionItemsKey(input.workspaceId, String(input.collectionId)), input] as const;

export const getCollectionItemsQueryOptions = (input: GetCollectionItemsInput) =>
  queryOptions({
    queryKey: collectionItemsPageKey(input),
    queryFn: ({ signal }) => getCollectionItems(input, signal),
    staleTime: 0,
  });

/**
 * Not a suspense query: every filter, page and sort value is part of the key, so
 * suspending on a change would swap the whole page for the authenticated
 * layout's PageLoader. `keepPreviousData` holds the previous response instead,
 * which is how the app's other keyed lists behave (see edit-workspace-users).
 * `throwOnError` keeps failures going to the error boundary, as suspense did.
 *
 * @public
 */
export const useCollectionItems = ({
  workspaceId: overrideWorkspaceId,
  collectionId,
  page,
  limit,
  conditions,
  orderBy,
  asc,
  queryConfig,
}: Omit<GetCollectionItemsInput, 'workspaceId'> & {
  workspaceId?: number;
  queryConfig?: QueryConfig<typeof getCollectionItemsQueryOptions>;
}) => {
  const workspaceId = overrideWorkspaceId ?? requireWorkspaceId();
  return useQuery({
    ...getCollectionItemsQueryOptions({ workspaceId, collectionId, page, limit, conditions, orderBy, asc }),
    placeholderData: keepPreviousData,
    throwOnError: true,
    ...queryConfig,
  });
};
